/**
 * Shared campaign icon system — WEB (lucide-react).
 *
 * Single source of truth for the campaign icon-name → glyph mapping used by
 * the announcement modal, contextual notice sheet, and the admin preview.
 * The matching native module (campaignIcons.native.tsx) renders SF Symbols
 * on iOS + lucide on Android; Metro/Vite resolves the right file per platform.
 *
 * Design-system rule: NO emoji icons (web/src/CLAUDE.md). This module exists
 * so the icon maps live in ONE place instead of being copy-pasted (with
 * emoji) into every campaign component.
 *
 * Keep CAMPAIGN_ICON_KEYS in sync with the icon <option> list in
 * AdminCampaignEditor.tsx. Parity with the native SF map is pinned by
 * tests/gate/test_campaigns_design_studio.py.
 */
import React from 'react';
import {
  Watch, Moon, Flame, Star, Clock, CheckCircle, Gift, Megaphone, Info, Bell,
  type LucideIcon,
} from 'lucide-react';

export const CAMPAIGN_LUCIDE_MAP: Record<string, LucideIcon> = {
  watch: Watch,
  rest: Moon,        // no yoga glyph in lucide; Moon is the rest/recovery metaphor
  fire: Flame,
  star: Star,
  clock: Clock,
  check: CheckCircle,
  gift: Gift,
  megaphone: Megaphone,
  info: Info,
  bell: Bell,
};

export const CAMPAIGN_ICON_KEYS = Object.keys(CAMPAIGN_LUCIDE_MAP);

export function CampaignIcon({
  name, size, color, strokeWidth = 1.5,
}: {
  name?: string;
  size: number;
  color: string;
  strokeWidth?: number;
}) {
  // 'cc' is the Coach Cedar monogram — a composite badge, not a glyph, so it
  // bypasses the lucide map and renders the shared CedarTeamBadge.
  if (name === 'cc') return <CedarTeamBadge size={size} color={color} />;
  const Icon = (name && CAMPAIGN_LUCIDE_MAP[name]) || Megaphone;
  return <Icon size={size} color={color} strokeWidth={strokeWidth} />;
}

/**
 * "CC" monogram badge for the Coach Cedar Team identity (survey consent,
 * survey card). Replaces the old tree emoji; matches the cedar-chat avatar.
 */
export function CedarTeamBadge({ size = 48, color = '#1F6C4F' }: { size?: number; color?: string }) {
  return (
    <div
      style={{
        width: size, height: size, borderRadius: size / 2, background: color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: Math.round(size * 0.375), fontWeight: 700, letterSpacing: 0.5,
      }}
    >
      CC
    </div>
  );
}
